type MaybeAdmins = {
  admins?: string[] | null;
};

type OrganizationLike = MaybeAdmins & {
  id: string;
};

type LeagueLike = MaybeAdmins & {
  id: string;
  organizationId: string;
};

type ProgramLike = {
  id: string;
  organizationId: string;
};

type TeamLike = {
  id: string;
  organizationId?: string | null;
  leagueId?: string | null;
};

type PlayerLike = {
  id: string;
  teamId: string;
};

type TeamPlayerLike = {
  teamID: string;
  playerID: string;
};

type RosterMembershipLike = {
  teamId: string;
  playerId: string;
};

type AdminScopeInput = {
  userRoles: string[];
  username: string;
  organizations?: OrganizationLike[];
  leagues?: LeagueLike[];
  programs?: ProgramLike[];
  teams?: TeamLike[];
  players?: PlayerLike[];
  teamPlayers?: TeamPlayerLike[];
  rosterMemberships?: RosterMembershipLike[];
};

export type AdminScope = {
  isGlobalAdmin: boolean;
  isScopedCommish: boolean;
  managedOrganizationIds: Set<string>;
  managedLeagueIds: Set<string>;
  managedProgramIds: Set<string>;
  managedTeamIds: Set<string>;
  managedPlayerIds: Set<string>;
  canCreateOrganizations: boolean;
  canManageOrganization: (organizationId?: string | null) => boolean;
  canCreateLeagueInOrganization: (organizationId?: string | null) => boolean;
  canManageLeague: (leagueId?: string | null, organizationId?: string | null) => boolean;
  canCreateProgramInOrganization: (organizationId?: string | null) => boolean;
  canManageProgram: (organizationId?: string | null) => boolean;
  canCreateTeamInScope: (organizationId?: string | null, leagueId?: string | null) => boolean;
  canManageTeam: (team?: TeamLike | null) => boolean;
  canManagePlayer: (player?: PlayerLike | null, teamIds?: Iterable<string>) => boolean;
};

const hasRole = (roles: string[], role: string) => roles.includes(role);

export const buildAdminScope = ({
  userRoles,
  username,
  organizations = [],
  leagues = [],
  programs = [],
  teams = [],
  players = [],
  teamPlayers = [],
  rosterMemberships = []
}: AdminScopeInput): AdminScope => {
  const isGlobalAdmin = hasRole(userRoles, "SuperAdmin");
  const isScopedCommish = !isGlobalAdmin && hasRole(userRoles, "Commish");

  if (isGlobalAdmin) {
    const managedOrganizationIds = new Set(organizations.map((organization) => organization.id));
    const managedLeagueIds = new Set(leagues.map((league) => league.id));
    const managedProgramIds = new Set(programs.map((program) => program.id));
    const managedTeamIds = new Set(teams.map((team) => team.id));
    const managedPlayerIds = new Set(players.map((player) => player.id));
    return {
      isGlobalAdmin,
      isScopedCommish,
      managedOrganizationIds,
      managedLeagueIds,
      managedProgramIds,
      managedTeamIds,
      managedPlayerIds,
      canCreateOrganizations: true,
      canManageOrganization: (organizationId) => Boolean(organizationId),
      canCreateLeagueInOrganization: (organizationId) => Boolean(organizationId),
      canManageLeague: (leagueId, organizationId) => Boolean(leagueId || organizationId),
      canCreateProgramInOrganization: (organizationId) => Boolean(organizationId),
      canManageProgram: (organizationId) => Boolean(organizationId),
      canCreateTeamInScope: (organizationId, leagueId) => Boolean(organizationId || leagueId),
      canManageTeam: (team) => Boolean(team?.id),
      canManagePlayer: (player) => Boolean(player?.id)
    };
  }

  const managedOrganizationIds = new Set<string>(
    organizations.filter((organization) => (organization.admins ?? []).includes(username)).map((organization) => organization.id)
  );
  const directlyManagedLeagueIds = leagues
    .filter((league) => (league.admins ?? []).includes(username))
    .map((league) => league.id);
  for (const league of leagues) {
    if ((league.admins ?? []).includes(username)) {
      managedOrganizationIds.add(league.organizationId);
    }
  }

  const managedLeagueIds = new Set<string>(directlyManagedLeagueIds);
  for (const league of leagues) {
    if (managedOrganizationIds.has(league.organizationId)) {
      managedLeagueIds.add(league.id);
    }
  }

  const managedProgramIds = new Set<string>(
    programs.filter((program) => managedOrganizationIds.has(program.organizationId)).map((program) => program.id)
  );

  const managedTeamIds = new Set<string>();
  for (const team of teams) {
    if (
      (team.organizationId && managedOrganizationIds.has(team.organizationId)) ||
      (team.leagueId && managedLeagueIds.has(team.leagueId))
    ) {
      managedTeamIds.add(team.id);
    }
  }

  const managedPlayerIds = new Set<string>(
    players.filter((player) => managedTeamIds.has(player.teamId)).map((player) => player.id)
  );
  for (const link of teamPlayers) {
    if (managedTeamIds.has(link.teamID)) {
      managedPlayerIds.add(link.playerID);
    }
  }
  for (const membership of rosterMemberships) {
    if (managedTeamIds.has(membership.teamId)) {
      managedPlayerIds.add(membership.playerId);
    }
  }

  const canManageOrganization = (organizationId?: string | null) => Boolean(organizationId && managedOrganizationIds.has(organizationId));
  const canCreateLeagueInOrganization = (organizationId?: string | null) => Boolean(organizationId && managedOrganizationIds.has(organizationId));
  const canManageLeague = (leagueId?: string | null, organizationId?: string | null) =>
    Boolean((leagueId && managedLeagueIds.has(leagueId)) || (organizationId && managedOrganizationIds.has(organizationId)));
  const canCreateProgramInOrganization = (organizationId?: string | null) => Boolean(organizationId && managedOrganizationIds.has(organizationId));
  const canManageProgram = (organizationId?: string | null) => Boolean(organizationId && managedOrganizationIds.has(organizationId));
  const canCreateTeamInScope = (organizationId?: string | null, leagueId?: string | null) =>
    Boolean((organizationId && managedOrganizationIds.has(organizationId)) || (leagueId && managedLeagueIds.has(leagueId)));
  const canManageTeam = (team?: TeamLike | null) =>
    Boolean(
      team &&
        ((team.organizationId && managedOrganizationIds.has(team.organizationId)) ||
          (team.leagueId && managedLeagueIds.has(team.leagueId)))
    );
  const canManagePlayer = (player?: PlayerLike | null, teamIds?: Iterable<string>) => {
    if (!player) {
      return false;
    }
    if (managedPlayerIds.has(player.id) || managedTeamIds.has(player.teamId)) {
      return true;
    }
    if (teamIds) {
      for (const teamId of teamIds) {
        if (managedTeamIds.has(teamId)) {
          return true;
        }
      }
    }
    return false;
  };

  return {
    isGlobalAdmin,
    isScopedCommish,
    managedOrganizationIds,
    managedLeagueIds,
    managedProgramIds,
    managedTeamIds,
    managedPlayerIds,
    canCreateOrganizations: false,
    canManageOrganization,
    canCreateLeagueInOrganization,
    canManageLeague,
    canCreateProgramInOrganization,
    canManageProgram,
    canCreateTeamInScope,
    canManageTeam,
    canManagePlayer
  };
};
